import type { TripPattern } from '@/features/trips/models';

/**
 * Sorts trip patterns by arrival time
 * @param tripPatternGroups - A record of trip patterns grouped by a key
 * @param arriveBy - Reverse sorting order when true (for arrival time-based sorting)
 * @returns A new record of trip patterns grouped by a key, sorted by arrival time
 */
export function sortTripPatternsByArrival(
    tripPatternGroups: Record<string, TripPattern[]>,
    arriveBy = false
): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => [
            key,
            [...tripPatterns].sort((a, b) => {
                const aEndTime = a.isEndingSoon ? a.expectedEndTime : a.aimedEndTime;
                const bEndTime = b.isEndingSoon ? b.expectedEndTime : b.aimedEndTime;
                const comparison = aEndTime.getTime() - bEndTime.getTime();
                return arriveBy ? comparison * -1 : comparison;
            })
        ])
    );
}

/**
 * Marks and filters expensive trip patterns
 * @param tripPatternGroups - A record of trip patterns grouped by a key
 * @param removeExpensiveTripPatterns - Whether to remove expensive trip patterns
 * @param maxPatternsPerHour - The maximum number of patterns per hour before marking and filtering expensive trip patterns
 * @returns The trip patterns with expensive trip patterns disabled
 */
export function markAndFilterExpensiveTripPatterns(
    tripPatternGroups: Record<string, TripPattern[]>,
    removeExpensiveTripPatterns = false,
    maxPatternsPerHour = 3
): Record<string, TripPattern[]> {
    return Object.fromEntries(
        Object.entries(tripPatternGroups).map(([key, tripPatterns]) => {
            if (removeExpensiveTripPatterns) {
                return [key, tripPatterns.filter(tripPattern => !tripPattern.isExpensiveTripPattern)];
            }

            // Group trip patterns by hour
            const tripPatternsByHour = tripPatterns.reduce(
                (acc, tripPattern) => {
                    const hour = tripPattern.aimedStartTime.getHours();
                    acc[hour] = [...(acc[hour] || []), tripPattern];
                    return acc;
                },
                {} as Record<number, TripPattern[]>
            );

            // Get all trip patterns that are expensive and have more than 5 items in the same hour
            const expensiveTripPatterns = Object.values(tripPatternsByHour)
                .filter(tripPatterns => tripPatterns.length > maxPatternsPerHour)
                .flatMap(tripPatterns => tripPatterns.map(tripPattern => tripPattern.id));

            return [
                key,
                tripPatterns.map(tripPattern => ({
                    ...tripPattern,
                    disabled:
                        tripPattern.isExpensiveTripPattern &&
                        expensiveTripPatterns.some(expensiveTripPattern => expensiveTripPattern === tripPattern.id)
                }))
            ];
        })
    );
}
